import { useState, useRef, useEffect, useMemo } from 'react';
import { pb, getFileUrl } from '../../../lib/pb';
import { useData } from '../../../context/DataContext';
import { calcBusinessHours, compressImage, pdfToImage } from '../../../lib/utils';
import { 
  CheckCircle2, Clock, MapPin, AlertCircle, 
  Camera, FileText, ChevronLeft, Layout, Save, Upload, Eye, Trash2, ChevronRight,
  TrendingUp, Calendar, Zap, X, Download, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../../state/useAuth';

export default function WorkerChantier({ chantier, onClose }) {
  const { assignments, settings, refresh } = useData();
  const { model: user } = useAuth();
  const [ch, setCh] = useState(chantier);
  const [uploading, setUploading] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [touchStart, setTouchStart] = useState(null);

  // Sync internal state if prop changes
  useEffect(() => {
    setCh(chantier);
    setCheckedIn(false);
    setCheckInTime(null);
  }, [chantier]);

  // Performance calculation
  const performance = useMemo(() => {
    if (!ch || !assignments) return null;
    const cAs = assignments.filter(a => a.chantier === ch.id);
    const consumed = cAs.reduce((acc, a) => {
      return acc + (a.hours_override || calcBusinessHours(a.start_date, a.end_date, settings?.business_hours, settings?.exceptional_closures) || 0);
    }, 0);
    const budget = ch.budget_hours || 0;
    const progress = budget > 0 ? Math.min(100, (consumed / budget) * 100) : 0;
    return { consumed: Math.round(consumed), budget, progress, isOver: consumed > budget && budget > 0 };
  }, [ch, assignments, settings]);

  // DASHBOARD LOGIC
  const myAssignmentsSorted = useMemo(() => {
    if (!user || !assignments) return [];
    return assignments
      .filter(a => {
        const u = a.user;
        if (Array.isArray(u)) return u.some(id => (typeof id === 'string' ? id : id.id) === user.id);
        return (typeof u === 'string' ? u : u?.id) === user.id;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [user, assignments]);

  const upcomingMissions = useMemo(() => {
    const now = new Date();
    // Keep missions that haven't ended yet
    return myAssignmentsSorted.filter(a => new Date(a.end_date || a.start_date) >= now);
  }, [myAssignmentsSorted]);

  const nextMission = upcomingMissions[0];
  const otherMissions = upcomingMissions.slice(1);

  const gallery = ch?.gallery || [];
  const docs = ch?.attachments || [];

  const allMedia = useMemo(() => {
    if (!ch) return [];
    const g = gallery.map(f => ({ url: getFileUrl(ch, f), name: f, type: 'gallery' }));
    const d = docs.filter(f => f.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/))
                  .map(f => ({ url: getFileUrl(ch, f), name: f, type: 'attachment' }));
    return [...g, ...d];
  }, [ch, gallery, docs]);

  // EMPTY STATE / DASHBOARD
  if (!ch) return (
    <div className="worker-dashboard" style={{ background: 'var(--w-bg)', minHeight: '100%' }}>
      <div style={{ padding: '30px 20px 24px' }}>
        <h3 className="w-page-title" style={{ fontSize: 26, marginBottom: 4 }}>Prêt pour la suite ?</h3>
        <p className="w-page-subtitle">Découvrez vos prochaines interventions.</p>
      </div>

      <div style={{ padding: '0 20px 100px' }}>
        {nextMission ? (
          <div style={{ marginBottom: 32 }}>
            <div className="w-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--w-accent-dark)' }}>
              <Zap size={14} fill="var(--w-accent)" stroke="none" /> Prochaine mission
            </div>
            
            <div 
              className="w-card"
              onClick={() => window.dispatchEvent(new CustomEvent('open-mission', { detail: nextMission }))}
              style={{
                background: '#fff',
                border: '2px solid var(--w-accent)',
                padding: '24px',
                cursor: 'pointer',
                boxShadow: '0 12px 30px -10px rgba(34, 197, 94, 0.15)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <span className="w-status" style={{ 
                  background: nextMission.type === 'rdv' ? '#f5f3ff' : 'var(--w-accent-light)', 
                  color: nextMission.type === 'rdv' ? '#7c3aed' : 'var(--w-accent-dark)',
                  textTransform: 'uppercase', fontSize: 10, fontWeight: 900
                }}>
                  {nextMission.type === 'rdv' ? 'Rendez-vous' : 'Chantier'}
                </span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--w-text)' }}>
                    {new Date(nextMission.start_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--w-muted)' }}>PLANIFIÉ</div>
                </div>
              </div>

              <h4 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 900, color: 'var(--w-text)', lineHeight: 1.3 }}>
                {nextMission.type === 'rdv' ? nextMission.title : (nextMission.expand?.chantier?.title || 'Mission Terrain')}
              </h4>
              
              {nextMission.expand?.chantier?.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--w-muted)', marginBottom: 24 }}>
                  <MapPin size={14} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextMission.expand.chantier.location}</span>
                </div>
              )}

              <div className="w-btn-primary" style={{ padding: '14px', fontSize: 15 }}>
                 Consulter les détails <ChevronRight size={18} />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-empty" style={{ background: '#fff', borderRadius: 24, padding: 40, border: '1px solid var(--w-border)' }}>
            <Calendar size={48} strokeWidth={1.5} />
            <h3>Aucune mission</h3>
            <p>Votre planning est libre pour le moment.</p>
          </div>
        )}

        {otherMissions.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div className="w-section-title" style={{ marginBottom: 16 }}>Suite de la journée</div>
            <div className="w-card" style={{ background: '#fff', overflow: 'hidden', padding: 0 }}>
              {otherMissions.map((m, idx) => (
                <div 
                  key={m.id}
                  onClick={() => window.dispatchEvent(new CustomEvent('open-mission', { detail: m }))}
                  style={{ 
                    display: 'flex', alignItems: 'center', gap: 15, padding: '16px 20px', 
                    cursor: 'pointer', borderBottom: idx === otherMissions.length - 1 ? 'none' : '1px solid var(--w-border)',
                    transition: 'background 0.2s'
                  }}
                  className="w-minimal-row"
                >
                  <div style={{ minWidth: 50, textAlign: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: m.type === 'rdv' ? '#7c3aed' : 'var(--w-accent-dark)' }}>
                      {new Date(m.start_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div style={{ height: 24, width: 1, background: 'var(--w-border)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--w-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.type === 'rdv' ? m.title : (m.expand?.chantier?.title || 'Mission')}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--w-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {m.type === 'rdv' ? 'Rendez-vous' : 'Chantier terrain'}
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--w-border)' }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // MISSION DETAILS VIEW (When a chantier is selected)
  const handleCheckIn = () => {
    setCheckInTime(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
    setCheckedIn(true);
  };

  const handleFinish = async () => {
    if (!window.confirm('Marquer cette intervention comme terminée ?')) return;
    try {
      await pb.collection('chantiers').update(ch.id, { status: 'completed' });
      setCh(prev => ({ ...prev, status: 'completed' }));
      refresh();
    } catch { alert('Erreur sauvegarde.'); }
  };

  const handleFileUpload = async (e, field) => {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.type === 'application/pdf') {
          const imgs = await pdfToImage(f);
          for (const img of (Array.isArray(imgs) ? imgs : [imgs])) {
            const fd = new FormData(); fd.append(field + '+', img);
            const updated = await pb.collection('chantiers').update(ch.id, fd);
            setCh(updated);
          }
        } else {
          let finalFile = f;
          if (f.type.startsWith('image/')) finalFile = await compressImage(f);
          const fd = new FormData(); fd.append(field + '+', finalFile);
          const updated = await pb.collection('chantiers').update(ch.id, fd);
          setCh(updated);
        }
      }
      refresh();
    } catch (err) { alert("Erreur d'envoi."); }
    finally { setUploading(false); }
  };

  const handleDownload = async (url, name) => {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const a = document.createElement('a'); 
      a.href = URL.createObjectURL(blob); a.download = name || 'file'; 
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) { window.open(url, '_blank'); }
  };

  const handlePrev = (e) => { if(e) { e.preventDefault(); e.stopPropagation(); } setLightboxIndex(prev => prev > 0 ? prev - 1 : allMedia.length - 1); };
  const handleNext = (e) => { if(e) { e.preventDefault(); e.stopPropagation(); } setLightboxIndex(prev => prev < allMedia.length - 1 ? prev + 1 : 0); };

  const handleTouchStart = (e) => { setTouchStart(e.targetTouches[0].clientX); };
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) handleNext(e);
    if (diff < -50) handlePrev(e);
    setTouchStart(null);
  };

  return (
    <div className="worker-chantier-page">
      <div className="w-hero-v2" style={{ background: ch.color || '#0f172a', position: 'relative', padding: '50px 24px 40px', color: '#fff', overflow: 'hidden' }}>
          <div className="w-hero-overlay" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)' }} />
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            style={{ 
              position: 'absolute', top: 16, right: 16, zIndex: 10,
              background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: '50%',
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer', backdropFilter: 'blur(5px)'
            }}
          >
            <X size={20} />
          </button>

          <div className="w-hero-content" style={{ position: 'relative', zIndex: 1 }}>
          <div className="w-status-row" style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
            <span className={`w-pill status-${ch.status}`} style={{ padding: '4px 12px', borderRadius: 100, fontSize: 10, fontWeight: 900, background: ch.status === 'completed' ? '#10b981' : 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)' }}>
              {ch.status === 'completed' ? '✓ Terminé' : ch.status === 'in_progress' ? 'En cours' : 'Programmé'}
            </span>
            <div className="w-client-tag" style={{ fontSize: 12, fontWeight: 700, opacity: 0.8 }}>{ch.client_name || 'Client AEF 32'}</div>
          </div>
          <h1 className="w-title-main" style={{ margin: '0 0 10px', fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em' }}>{ch.title}</h1>

          {ch.assignmentStart && (
            <div className="w-time-card-v3" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', padding: '16px', borderRadius: 20, marginBottom: 20, border: '1px solid rgba(255,255,255,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Clock size={18} />
                <span style={{ fontSize: 14, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Planning de Mission</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 800 }}>
                  <span style={{ opacity: 0.7 }}>Du</span> {new Date(ch.assignmentStart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  <span style={{ opacity: 0.7, margin: '0 8px' }}>au</span> {new Date(ch.assignmentEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                </div>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase' }}>Journée Type</span>
                    <span style={{ fontSize: 16, fontWeight: 900 }}>
                      {(() => {
                        const d = new Date(ch.assignmentStart);
                        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()];
                        const config = settings?.business_hours?.[dayName] || { open: '08:00', close: '17:00' };
                        return `${config.open} — ${config.close}`;
                      })()}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase' }}>Fin du chantier</span>
                    <span style={{ fontSize: 16, fontWeight: 900, color: '#fcd34d' }}>
                      {new Date(ch.assignmentEnd).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="w-loc-row" style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.9, fontSize: 13, fontWeight: 600, marginBottom: 15 }}>
            <MapPin size={16} />
            <span>{ch.location || 'Localisation non définie'}</span>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ch.location || '')}`, '_blank')}
              className="w-card"
              style={{ flex: 1, padding: '12px', background: '#4285F4', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 900 }}
            >
              <MapPin size={16} /> Google Maps
            </button>
            <button
              onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(ch.location || '')}&navigate=yes`, '_blank')}
              className="w-card"
              style={{ flex: 1, padding: '12px', background: '#33ccff', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 900 }}
            >
              < Zap size={16} fill="#fff" stroke="none" /> Waze
            </button>
          </div>
        </div>
      </div>

      <div className="w-main-scroll" style={{ padding: '24px 16px 100px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {performance && performance.budget > 0 && (
          <div className="w-card" style={{ padding: 20 }}>
            <div className="w-section-title" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 15 }}>
              <Clock size={16} /> Suivi du Budget Temps
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 15 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{performance.consumed}h</div>
                <div className="w-page-subtitle" style={{ fontSize: 10 }}>Consommé</div>
              </div>
              <div style={{ width: 1, background: 'var(--w-border)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{performance.budget}h</div>
                <div className="w-page-subtitle" style={{ fontSize: 10 }}>Budget</div>
              </div>
            </div>
            <div style={{ height: 10, background: 'var(--w-bg)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: performance.isOver ? '#ef4444' : 'var(--w-accent)', width: `${performance.progress}%` }} />
            </div>
          </div>
        )}

        {!checkedIn ? (
          <button className="w-btn-primary" onClick={handleCheckIn} style={{ padding: 20 }}>
            <MapPin size={22} /> JE SUIS SUR PLACE
          </button>
        ) : (
          <div className="w-status completed" style={{ padding: 20, borderRadius: 20, display: 'flex', gap: 15, width: 'auto' }}>
            <CheckCircle2 size={24} />
            <div>
              <div style={{ fontWeight: 800 }}>Check-in confirmé</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Arrivée à {checkInTime}</div>
            </div>
          </div>
        )}

        {ch.assignmentNotes && (
          <div className="w-card" style={{ padding: 20, background: '#fffbeb' }}>
            <div className="w-section-title">Instructions</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#92400e', lineHeight: 1.6 }}>{ch.assignmentNotes}</div>
          </div>
        )}

        <div className="w-card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <div className="w-section-title">Photos du chantier ({gallery.length})</div>
            <label style={{ cursor: 'pointer', color: 'var(--w-accent)', fontWeight: 800, fontSize: 13 }}>
              + AJOUTER
              <input type="file" multiple accept="image/*" onChange={e => handleFileUpload(e, 'gallery')} hidden disabled={uploading} />
            </label>
          </div>
          <div className="w-media-scroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 15 }}>
            {gallery.map(img => {
              const url = getFileUrl(ch, img);
              return (
                <div key={img} style={{ position: 'relative', flexShrink: 0 }}>
                   <img 
                     src={url} 
                     alt="chantier" 
                     onClick={() => setLightboxIndex(allMedia.findIndex(m => m.url === url))}
                     style={{ width: 140, height: 140, borderRadius: 16, objectFit: 'cover', border: '1px solid var(--w-border)', cursor: 'pointer' }} 
                   />
                   <button 
                     onClick={(e) => { e.stopPropagation(); handleDownload(url, img); }}
                     style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                   >
                     <Download size={18} color="var(--w-accent)" />
                   </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="w-card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <div className="w-section-title">Plans & Documents ({docs.length})</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ cursor: 'pointer', color: '#0ea5e9', fontWeight: 800, fontSize: 13 }}>
                + PDF
                <input type="file" multiple accept=".pdf" onChange={e => handleFileUpload(e, 'attachments')} hidden disabled={uploading} />
              </label>
              <label style={{ cursor: 'pointer', color: '#f59e0b', fontWeight: 800, fontSize: 13 }}>
                + PHOTO PLAN
                <input type="file" accept="image/*" capture="environment" onChange={e => handleFileUpload(e, 'attachments')} hidden disabled={uploading} />
              </label>
            </div>
          </div>
          {docs.length > 0 ? (
            <div className="w-media-scroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 15 }}>
              {docs.map(f => {
                const url = getFileUrl(ch, f);
                const isImage = f.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                return (
                  <div key={f} style={{ position: 'relative', flexShrink: 0 }}>
                    {isImage ? (
                      <img 
                        src={url} 
                        alt="plan" 
                        onClick={() => setLightboxIndex(allMedia.findIndex(m => m.url === url))}
                        style={{ width: 140, height: 140, borderRadius: 16, objectFit: 'cover', border: '1px solid var(--w-border)', cursor: 'pointer' }} 
                      />
                    ) : (
                      <div 
                        onClick={() => window.open(url, '_blank')}
                        style={{ width: 140, height: 140, borderRadius: 16, background: '#f8fafc', border: '1px solid var(--w-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      >
                        <FileText size={32} color="#64748b" />
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', marginTop: 8, textAlign: 'center', padding: '0 10px', wordBreak: 'break-all' }}>{f.split('_').pop()}</span>
                      </div>
                    )}
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDownload(url, f); }}
                      style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                    >
                      <Download size={18} color="#0ea5e9" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Aucun document.</div>
          )}
        </div>

        {ch.status !== 'completed' && (
          <button className="w-btn-primary" onClick={handleFinish} style={{ background: 'var(--w-text)', marginTop: 20 }}>
            TERMINER L'INTERVENTION
          </button>
        )}
      </div>
      {/* LIGHTBOX SLIDER */}
      {lightboxIndex !== null && allMedia[lightboxIndex] && (
        <div 
          onClick={() => setLightboxIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.98)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            style={{ position: 'absolute', top: 30, right: 30, background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 100 }}
          >
            <X size={24} />
          </button>
          
          <button 
            type="button"
            onClick={handlePrev} 
            style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: '50%', width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 100 }}
          >
            <ChevronLeft size={36} />
          </button>
          
          <button 
            type="button"
            onClick={handleNext} 
            style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', borderRadius: '50%', width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 100 }}
          >
            <ChevronRight size={36} />
          </button>

          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img 
              src={allMedia[lightboxIndex].url} 
              alt="Fullscreen" 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, boxShadow: '0 20px 50px rgba(0,0,0,0.5)', transition: 'all 0.3s' }} 
            />
            
            <div style={{ position: 'absolute', top: 0, left: 0, right:0, textAlign: 'center', color: '#fff', fontSize: 13, fontWeight: 700, padding: 20, opacity: 0.8 }}>
               {lightboxIndex + 1} / {allMedia.length}
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 40, display: 'flex', gap: 15 }}>
             <button 
               onClick={(e) => { e.stopPropagation(); handleDownload(allMedia[lightboxIndex].url, allMedia[lightboxIndex].name); }}
               style={{ background: 'var(--w-accent)', color: '#fff', border: 'none', borderRadius: 15, padding: '12px 25px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}
             >
               <Download size={20} /> Télécharger
             </button>
             <button 
               onClick={(e) => { e.stopPropagation(); window.open(allMedia[lightboxIndex].url, '_blank'); }}
               style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: 15, padding: '12px 25px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}
             >
               <ExternalLink size={20} /> Ouvrir
             </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-slow { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.95); } }
        .pulse-slow { animation: pulse-slow 3s infinite; }
        
        .w-media-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .w-media-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .w-media-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .w-media-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}
